import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import RollText from '@/components/generals/roll-text'
import SectionLabel from '@/components/generals/section-label'
import Magnetic from '@/components/motion/magnetic'
import SplitReveal from '@/components/motion/split-reveal'
import TransitionLink from '@/components/motion/transition-link'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import { concepts } from '@/constants/content'

interface PreviewCtaProps {
    index?: string
}

// Resting position of each concept card once the fan has opened.
const FAN = [
    { x: -34, y: 10, r: -16 },
    { x: -22, y: 2, r: -10 },
    { x: -10, y: -3, r: -4 },
    { x: 2, y: -5, r: 2 },
    { x: 14, y: -2, r: 8 },
    { x: 26, y: 4, r: 13 },
    { x: 37, y: 12, r: 19 }
]

// The closing offer: a free homepage concept. Real concepts from the studio
// start as one tight stack and fan open as the section arrives.
const PreviewCta: FC<PreviewCtaProps> = ({ index }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const cards = section.querySelectorAll<HTMLElement>('[data-concept]')
        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            cards.forEach((card, cardIndex) => {
                const target = FAN[cardIndex % FAN.length]

                gsap.fromTo(card, { xPercent: 0, yPercent: 30, rotation: 0, scale: 0.86 }, {
                    xPercent: target.x * 1.7,
                    yPercent: target.y * 2,
                    rotation: target.r,
                    scale: 1,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 85%',
                        end: 'center 55%',
                        scrub: 0.8
                    }
                })
            })
        })

        mm.add('(prefers-reduced-motion: reduce)', () => {
            cards.forEach((card, cardIndex) => {
                const target = FAN[cardIndex % FAN.length]
                gsap.set(card, { xPercent: target.x * 1.7, yPercent: target.y * 2, rotation: target.r })
            })
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section
            id='preview'
            ref={sectionRef}
            data-scene='blue'
            aria-labelledby='preview-title'
            className='relative flex min-h-svh flex-col justify-center gap-16 overflow-hidden px-5 py-28 md:px-8 md:py-36 lg:grid lg:grid-cols-12 lg:items-center lg:gap-10'
        >
            <div className='relative z-10 lg:col-span-6'>
                <SectionLabel index={index}>
                    Free homepage preview
                </SectionLabel>

                <SplitReveal as='h2' id='preview-title' className='type-h1 mt-8'>
                    See what your website could <em className='type-serif text-pop'>become.</em>
                </SplitReveal>

                <p className='type-lead mt-8 max-w-[40ch] text-quiet'>
                    {"Send us your website and we'll show how your homepage could work harder: a free concept in Figma, before you commit to a full project."}
                </p>

                <div className='mt-12'>
                    <Magnetic>
                        <TransitionLink
                            href='/contact?service=free-homepage-preview'
                            className='group inline-flex items-center gap-4 bg-volt px-7 py-5 text-base font-medium text-ink md:text-lg'
                        >
                            <RollText>
                                Request a homepage preview
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-500 ease-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                        </TransitionLink>
                    </Magnetic>

                    <p className='type-label mt-5 text-quiet'>
                        No commitment. See the potential.
                    </p>
                </div>
            </div>

            <div aria-hidden='true' className='relative h-[46vh] min-h-[300px] lg:col-span-6 lg:h-[70vh]'>
                {concepts.map((concept, cardIndex) => (
                    <div
                        key={concept.src}
                        data-concept=''
                        className='absolute left-1/2 top-1/2 -ml-[min(19vw,210px)] -mt-[min(14vw,150px)] w-[min(38vw,420px)] origin-bottom border border-white/20 bg-ink p-1.5 shadow-[0_30px_60px_-20px_rgb(0_0_0/0.45)] max-lg:-ml-[34vw] max-lg:-mt-[24vw] max-lg:w-[68vw]'
                        style={{ zIndex: cardIndex === 3 ? 10 : 5 - Math.abs(cardIndex - 3) }}
                    >
                        <img src={concept.src} alt='' loading='lazy' decoding='async' className='aspect-[4/3] w-full object-cover' />
                    </div>
                ))}
            </div>
        </section>
    )
}

export default PreviewCta
